import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { convertHeicToJpeg, isHeic } from "@/lib/heicConvert";
import { toast } from "sonner";
import { Upload, Images, Scissors, Merge, Trash2, Check, X, Search, Loader2, ArrowRight, GripVertical, Clock, Save, Trash, Undo2, Redo2, Download, PackageOpen, Circle, History } from "lucide-react";
import exifr from "exifr";
import { saveDraft, loadDraft, clearDraft, saveHistorySnapshot } from "@/lib/bulkPhotoDraft";
import { exportDraftZip, downloadZipBlob, importDraftZip } from "@/lib/bulkPhotoRescue";
import { useUndoStack } from "@/lib/useUndoStack";
import BulkSorterErrorBoundary from "@/components/BulkSorterErrorBoundary";
import { OriginHealthDot } from "@/components/OriginHealthDot";
import { RescueHistoryPanel } from "@/components/RescueHistoryPanel";
import { isMobile } from "@/lib/isMobile";

// Stages a "not-yet-photographed" or "in-shoot" piece typically sits at. We
// prioritise these in the SKU dropdown so the client doesn't scroll past sold
// or discontinued pieces while attaching. Any active-stage piece is still
// selectable via the search input.
const CANDIDATE_STAGES = ["Sourcing", "Photographing", "Studio Editing", "Listing & Marketing"];

// Time gap (seconds) between two consecutive photos that we treat as a "new
// piece". iPhone bursts of the same piece typically sit inside a 30-90s window,
// then a natural pause happens when the shooter moves to the next hanger.
const DEFAULT_CLUSTER_GAP_S = 60;

// Small helper — sortable stable key on the raw File so React doesn't churn.
let photoUid = 0;
const nextPhotoId = () => `p_${Date.now()}_${photoUid++}`;

// ------------------------------------------------------------------ Photo tile
// Wrapped in React.memo so a keystroke in a nearby SkuPicker (or any parent
// re-render) doesn't force EVERY photo tile on the page to re-render. At 30
// clusters × 6 photos = 180 tiles, that difference is the difference between
// a snappy drag and a laggy one.
const PhotoTile = React.memo(function PhotoTile({ photo, onRemove, selected, onToggleSelect, onDragStart, onBrokenPreview, broken }) {
  return (
    <div
      draggable={!broken}
      onDragStart={(e) => !broken && onDragStart?.(e, photo)}
      onClick={onToggleSelect ? () => onToggleSelect(photo.id) : undefined}
      data-testid={`bp-photo-${photo.id}`}
      data-broken={broken ? "1" : undefined}
      className={`relative group w-24 h-24 flex-shrink-0 border ${selected ? "border-brass ring-2 ring-brass/40" : broken ? "border-red-400 border-dashed" : "border-taupe/40"} bg-ivory ${broken ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"} overflow-hidden`}
      title={
        broken
          ? `${photo.name} · couldn't be re-loaded from local storage — please re-upload this photo`
          : photo.name + (photo.takenAt ? ` · ${new Date(photo.takenAt).toLocaleTimeString()}` : "")
      }
    >
      {broken ? (
        // Graceful placeholder for a dead blob: Safari occasionally truncates
        // large Blobs stored in IndexedDB after a tab close/reopen, leaving
        // the previewUrl pointing at empty content. Show a clear "unreadable"
        // tile with the original filename so the user knows exactly what to
        // re-drop, instead of the browser's ugly broken-image icon.
        <div className="w-full h-full flex flex-col items-center justify-center text-red-700 bg-red-50 p-1 text-center">
          <X className="w-4 h-4 mb-1" />
          <span className="text-[0.55rem] leading-tight truncate w-full font-mono">{photo.name?.slice(-14) || "unreadable"}</span>
        </div>
      ) : (
        <img
          src={photo.previewUrl}
          alt=""
          className="w-full h-full object-cover pointer-events-none"
          onError={() => onBrokenPreview?.(photo.id)}
        />
      )}
      {selected && !broken && (
        <div className="absolute inset-0 bg-brass/20 flex items-center justify-center pointer-events-none">
          <Check className="w-6 h-6 text-brass" />
        </div>
      )}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(photo.id); }}
          className="absolute top-1 right-1 bg-ivory/90 hover:bg-red-100 border border-taupe/60 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`bp-photo-remove-${photo.id}`}
          title="drop photo"
        >
          <X className="w-3 h-3 text-red-700" />
        </button>
      )}
    </div>
  );
});

// -------------------------------------------------------- Searchable SKU picker
// Also memoised — a keystroke in one picker used to trigger a re-render of
// every SkuPicker on the page (30+ dropdowns computing their filtered lists).
// With React.memo, only the one being interacted with re-runs its useMemo.
const SkuPicker = React.memo(function SkuPicker({ items, value, onChange, testid, autoSuggestId, disabled }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const selected = items.find((i) => i.id === value);
  const suggestion = autoSuggestId && !value ? items.find((i) => i.id === autoSuggestId) : null;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) {
      // Show candidate-stage items first, then everything else.
      const primary = items.filter((i) => CANDIDATE_STAGES.includes(i.stage || "")).slice(0, 40);
      return primary.length ? primary : items.slice(0, 40);
    }
    return items
      .filter((i) => `${i.sku || ""} ${i.title || ""} ${i.brand || ""}`.toLowerCase().includes(term))
      .slice(0, 40);
  }, [items, q]);

  return (
    <div className="relative flex-1 min-w-0" data-testid={testid}>
      <div
        onClick={disabled ? undefined : () => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className={`border ${value ? "border-brass" : "border-taupe/60"} bg-ivory px-3 py-2 ${disabled ? "opacity-70 cursor-default" : "cursor-text"} flex items-center gap-2 min-w-0`}
      >
        <Search className="w-3 h-3 text-taupe flex-shrink-0" />
        {selected ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="font-mono text-xs text-brass flex-shrink-0">{selected.sku || selected.id.slice(0, 6)}</span>
            <span className="text-sm text-charcoal truncate">{selected.title || "(untitled)"}</span>
            {!disabled && (
              <button onClick={(e) => { e.stopPropagation(); onChange(null); setQ(""); }} className="ml-auto text-taupe hover:text-red-700 flex-shrink-0" data-testid={`${testid}-clear`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : (
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            placeholder={suggestion ? `suggested: ${suggestion.sku || suggestion.id.slice(0, 6)} · ${suggestion.title || ""}` : "search sku, title, or brand…"}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-taupe/70"
            data-testid={`${testid}-input`}
          />
        )}
      </div>
      {open && !selected && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-1 bg-ivory border border-taupe/60 shadow-2xl max-h-72 overflow-y-auto z-20" data-testid={`${testid}-menu`}>
            {suggestion && !q && (
              <button
                onClick={() => { onChange(suggestion.id); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 hover:bg-linen bg-brass/5 border-b border-taupe/30 flex items-start gap-3"
                data-testid={`${testid}-suggest`}
              >
                <span className="text-[0.6rem] uppercase tracking-widest text-brass border border-brass/60 px-1.5 py-0.5 mt-0.5">next</span>
                <div className="min-w-0">
                  <p className="font-mono text-xs text-brass">{suggestion.sku || suggestion.id.slice(0, 6)}</p>
                  <p className="text-sm text-charcoal truncate">{suggestion.title || "(untitled)"}</p>
                  <p className="text-[0.65rem] uppercase tracking-widest text-taupe mt-0.5">{suggestion.stage} · {suggestion.brand || "—"}</p>
                </div>
              </button>
            )}
            {filtered.length === 0 && <p className="p-4 editorial-label text-taupe">no match. try another search term.</p>}
            {filtered.map((i) => (
              <button
                key={i.id}
                onClick={() => { onChange(i.id); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 hover:bg-linen border-b border-taupe/20 last:border-b-0 flex items-start gap-3"
              >
                <span className="font-mono text-xs text-brass w-14 flex-shrink-0 mt-0.5">{i.sku || i.id.slice(0, 6)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-charcoal truncate">{i.title || "(untitled)"}</p>
                  <p className="text-[0.65rem] uppercase tracking-widest text-taupe mt-0.5">{i.stage} · {i.brand || "—"} · {(i.photos || []).length} photos</p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

// ------------------------------------------------------------------- Main page
function AdminBulkPhotosInner() {
  const [items, setItems] = useState([]);      // full inventory for search
  const [clusters, setClusters] = useState([]);// [{id, photos:[...], targetItemId, savedAt}]
  const [unassigned, setUnassigned] = useState([]); // photos not in any cluster
  const [gap, setGap] = useState(DEFAULT_CLUSTER_GAP_S);
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState({}); // { clusterId: Set(photoIds) }
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftStatus, setDraftStatus] = useState({ savedAt: null, saving: false });
  // Render at most PAGE_SIZE clusters at a time. Above that (e.g. 76 clusters
  // from a 458-photo shoot) rendering every cluster card at once locks the tab
  // for seconds and makes clicks feel unresponsive.
  const [page, setPage] = useState(0);
  const onMobile = isMobile();
  // Mobile Safari kills tabs at ~1.5GB memory. Shrink DOM + upload footprint
  // aggressively so a 6-9 HEIC-photo cluster attach doesn't blank the tab.
  const PAGE_SIZE = onMobile ? 10 : 30;
  const UPLOAD_CONCURRENCY = onMobile ? 1 : 3;
  // Live progress line during save-all so the user knows the tab is alive
  // during the ~10-30s a HEIC-heavy attach can take.
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, sku }
  // Track photo IDs whose blob preview URL failed to load. This happens most
  // often on Safari after a tab reopen when IndexedDB has silently truncated
  // large stored Blobs — the createObjectURL succeeds but the URL resolves to
  // empty content. We surface these tiles with a red dashed placeholder + a
  // top-level summary so the user can discard and re-upload just those,
  // rather than seeing a wall of broken-image icons with no explanation.
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  const markBroken = useCallback((photoId) => {
    setBrokenIds((prev) => {
      if (prev.has(photoId)) return prev;
      const next = new Set(prev);
      next.add(photoId);
      return next;
    });
  }, []);
  // When an upload fails, remember the timestamp so the origin-health badge
  // downgrades to red immediately (rather than waiting for its next poll).
  const [lastUploadFailureAt, setLastUploadFailureAt] = useState(null);
  // Rescue history sheet visibility.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Hide saved clusters by default once we cross a small threshold — they
  // otherwise pile up on-screen at scale (76 clusters × 6 tiles = 456 img
  // tags), hurt scroll perf, and hold their preview blobs in memory. When
  // hidden they render as compact chips instead.
  const [hideSaved, setHideSaved] = useState(true);

  // -------- Undo stack ------------------------------------------------------
  // Snapshot / restore just the tracked, user-mutable state. previewUrl blobs
  // are re-used (same File refs), so snapshots are cheap.
  const stateRef = useRef({ clusters, unassigned });
  stateRef.current = { clusters, unassigned };
  const getSnapshot = useCallback(() => ({
    clusters: stateRef.current.clusters.map((c) => ({ ...c, photos: [...c.photos] })),
    unassigned: [...stateRef.current.unassigned],
  }), []);
  const applySnapshot = useCallback((snap) => {
    setClusters(snap.clusters);
    setUnassigned(snap.unassigned);
    setSelection({});
  }, []);
  const undo = useUndoStack({ getSnapshot, applySnapshot });

  useEffect(() => {
    api.get("/inventory").then((r) => setItems(r.data || [])).catch(() => toast.error("couldn't load inventory."));
  }, []);

  // -------- Load any previously-saved draft from IndexedDB on mount --------
  // This is what makes "close laptop → come back tomorrow" work. All the raw
  // File blobs are stored client-side, and we re-generate object URLs on load
  // (the previous URLs were revoked or invalidated by the reload).
  useEffect(() => {
    let cancelled = false;
    loadDraft().then((draft) => {
      if (cancelled || !draft) { setDraftLoaded(true); return; }
      // Filter out saved clusters (safely persisted server-side already) so
      // we don't restore stale saved state that no longer matches production.
      const unsaved = (draft.clusters || []).filter((c) => !c.savedAt);
      setClusters(unsaved);
      setUnassigned(draft.unassigned || []);
      if (typeof draft.gap === "number") setGap(draft.gap);
      const totalPhotos = unsaved.reduce((n, c) => n + c.photos.length, 0) + (draft.unassigned || []).length;
      if (totalPhotos > 0) {
        toast.success(`draft restored · ${totalPhotos} photos, ${unsaved.length} clusters — pick up where you left off.`);
      }
      setDraftStatus({ savedAt: draft.updatedAt, saving: false });
      setDraftLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  // -------- Auto-save to IndexedDB whenever state changes (debounced) -------
  // Debounced by 800ms so a rapid burst of drops / drags doesn't flood IDB.
  // We wait until draftLoaded to avoid overwriting on mount before the load
  // effect has had a chance to hydrate.
  useEffect(() => {
    if (!draftLoaded) return;
    // Freeze auto-save while an attach is in-flight — on mobile Safari the
    // IDB serialisation competes with canvas/decode for main-thread memory
    // and can tip the tab into OOM (blank page). Draft will resume saving
    // as soon as busy flips back to false.
    if (busy) return;
    // Only persist unsaved work — saved clusters are safely on the server.
    const draftClusters = clusters.filter((c) => !c.savedAt);
    if (draftClusters.length === 0 && unassigned.length === 0) {
      // Nothing to remember — wipe the record so a fresh visit is truly fresh.
      const t = setTimeout(() => { clearDraft(); setDraftStatus({ savedAt: null, saving: false }); }, 400);
      return () => clearTimeout(t);
    }
    setDraftStatus((s) => ({ ...s, saving: true }));
    const t = setTimeout(async () => {
      const ok = await saveDraft({ clusters: draftClusters, unassigned, gap });
      setDraftStatus({ savedAt: ok ? Date.now() : null, saving: false });
    }, 2500);
    return () => clearTimeout(t);
  }, [clusters, unassigned, gap, draftLoaded, busy]);

  // -------- Warn on tab close when unsaved work exists ---------------------
  useEffect(() => {
    const totalUnsaved = clusters.filter((c) => !c.savedAt).reduce((n, c) => n + c.photos.length, 0) + unassigned.length;
    if (totalUnsaved === 0) return;
    const handler = (e) => {
      e.preventDefault();
      // Modern browsers ignore the message and show their own text; the presence
      // of `returnValue` is what triggers the confirm dialog.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [clusters, unassigned]);

  // -------- Keyboard shortcuts: cmd/ctrl-Z for undo, cmd-shift-Z/ctrl-Y for redo --
  useEffect(() => {
    const handler = (e) => {
      // Ignore when the user is typing in the SKU search or gap input.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const key = e.key.toLowerCase();
      // Redo: cmd-shift-z (mac convention) or ctrl-y (windows convention).
      if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        undo.redo().then((label) => { if (label) toast.success(`redid: ${label}`); });
        return;
      }
      if (key === "z") {
        e.preventDefault();
        undo.undo().then((label) => { if (label) toast.success(`undid: ${label}`); });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo]);

  // Free all preview object URLs on unmount to avoid leaking Blob refs across a
  // large shoot. Rely on refs so we don't re-run the effect on every state change.
  const clustersRef = useRef(clusters);
  const unassignedRef = useRef(unassigned);
  clustersRef.current = clusters;
  unassignedRef.current = unassigned;
  useEffect(() => () => {
    clustersRef.current.forEach((c) => c.photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)));
    unassignedRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  }, []);

  // Refresh a single item's photos so the SKU picker's photo count stays honest.
  const refetchItem = async (itemId) => {
    try {
      const { data } = await api.get(`/inventory/${itemId}`);
      setItems((prev) => prev.map((i) => (i.id === itemId ? data : i)));
    } catch { /* silent */ }
  };

  // ---------- Ingest photos and cluster by EXIF timestamp ----------
  const ingest = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    const arr = Array.from(fileList);
    const heicCount = arr.filter(isHeic).length;
    if (heicCount > 0) {
      toast.info(`converting ${heicCount} iphone HEIC photo${heicCount === 1 ? "" : "s"}…`);
    }
    try {
      // Read EXIF + convert HEIC in bounded chunks. A raw Promise.all on 400+
      // files simultaneously loads every blob into memory and spikes CPU on
      // the main thread; chunks of 20 keep the tab responsive.
      const CHUNK = 20;
      const enriched = [];
      for (let start = 0; start < arr.length; start += CHUNK) {
        const chunk = arr.slice(start, start + CHUNK);
        const results = await Promise.all(
          chunk.map(async (raw) => {
            let takenAt = null;
            try {
              const meta = await exifr.parse(raw, ["DateTimeOriginal", "CreateDate"]);
              takenAt = (meta?.DateTimeOriginal || meta?.CreateDate || null);
            } catch { /* no exif */ }
            const f = isHeic(raw) ? await convertHeicToJpeg(raw) : raw;
            return {
              id: nextPhotoId(),
              file: f,
              name: f.name,
              size: f.size,
              takenAt: takenAt ? new Date(takenAt).getTime() : null,
              previewUrl: URL.createObjectURL(f),
            };
          }),
        );
        enriched.push(...results);
        if (arr.length > CHUNK && start + CHUNK < arr.length) {
          // Give the UI a beat to render — otherwise a 400-file drop freezes
          // the tab for several seconds.
          await new Promise((r) => setTimeout(r, 0));
        }
      }

      // Sort by EXIF ts (nulls last), then greedy-cluster on gap.
      const sorted = [...enriched].sort((a, b) => {
        if (a.takenAt == null && b.takenAt == null) return 0;
        if (a.takenAt == null) return 1;
        if (b.takenAt == null) return -1;
        return a.takenAt - b.takenAt;
      });

      const nextClusters = [];
      let currentBucket = null;
      const noExif = [];
      for (const p of sorted) {
        if (p.takenAt == null) { noExif.push(p); continue; }
        if (!currentBucket) {
          currentBucket = { id: `c_${Date.now()}_${nextClusters.length}`, photos: [p], targetItemId: null, savedAt: null };
          nextClusters.push(currentBucket);
          continue;
        }
        const last = currentBucket.photos[currentBucket.photos.length - 1];
        const gapS = (p.takenAt - last.takenAt) / 1000;
        if (gapS <= gap) {
          currentBucket.photos.push(p);
        } else {
          currentBucket = { id: `c_${Date.now()}_${nextClusters.length}`, photos: [p], targetItemId: null, savedAt: null };
          nextClusters.push(currentBucket);
        }
      }

      // Snapshot BEFORE we mutate so undo returns to the pre-drop state.
      undo.push(`drop ${enriched.length} photos`);
      setClusters((prev) => [...prev, ...nextClusters]);
      setUnassigned((prev) => [...prev, ...noExif]);
      toast.success(`${enriched.length} photos loaded · ${nextClusters.length} auto-clusters · ${noExif.length} without exif time.`);
    } finally {
      setBusy(false);
    }
  };

  // ---------- Visible clusters (respects the hide-saved toggle) ----------
  // Rendered separately from `clusters` so save/undo/etc still operate on the
  // full array while the on-screen list stays lean.
  const visibleClusters = useMemo(
    () => (hideSaved ? clusters.filter((c) => !c.savedAt) : clusters),
    [clusters, hideSaved],
  );
  const savedClusters = useMemo(
    () => clusters.filter((c) => c.savedAt && c.savedAt !== "saving"),
    [clusters],
  );

  // ---------- Draft rescue: export / import as ZIP ----------
  // Round-trip a wedged mobile draft to a Mac. The ZIP contains every
  // photo blob organised by cluster plus a manifest.json describing SKU
  // picks and the cluster gap. Import restores state; export never mutates.
  const [rescueBusy, setRescueBusy] = useState(null); // "export" | "import" | null
  const [rescuePct, setRescuePct] = useState(0);
  const rescueFileInputRef = useRef(null);

  const totalDraftPhotos = useMemo(
    () =>
      clusters.filter((c) => !c.savedAt).reduce((n, c) => n + c.photos.length, 0) +
      unassigned.length,
    [clusters, unassigned],
  );

  const handleDownloadDraft = useCallback(async () => {
    if (rescueBusy) return;
    if (totalDraftPhotos === 0) {
      toast.error("nothing to export — the draft is empty.");
      return;
    }
    // Guardrail: very large drafts occasionally choke Mobile Safari during
    // final blob assembly. Warn once so the user isn't surprised by a hang.
    if (totalDraftPhotos > 300) {
      const proceed = window.confirm(
        `heads up — you're exporting ${totalDraftPhotos} photos. on wifi this takes 1-3 minutes and needs ~2-3× the draft size in temporary memory. continue?`,
      );
      if (!proceed) return;
    }
    setRescueBusy("export");
    setRescuePct(0);
    try {
      const blob = await exportDraftZip(
        { clusters, unassigned, gap },
        (pct) => setRescuePct(Math.round(pct)),
      );
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      downloadZipBlob(blob, `maison-pilet-draft-${stamp}.zip`);
      // Also snapshot into the in-browser Rescue History so a mis-clicked
      // clear later on can be recovered without needing the ZIP file.
      await saveHistorySnapshot({ clusters, unassigned, gap, source: "export" });
      toast.success(`draft exported · ${totalDraftPhotos} photos zipped.`);
    } catch (err) {
      console.error("draft export failed", err);
      toast.error(`export failed: ${err?.message || "unknown error"}`);
    } finally {
      setRescueBusy(null);
      setRescuePct(0);
    }
  }, [rescueBusy, totalDraftPhotos, clusters, unassigned, gap]);

  const handleImportDraft = useCallback(async (file) => {
    if (!file || rescueBusy) return;
    // Warn if the user already has unsaved work — importing replaces the
    // current draft (mixing two would create duplicate photo IDs and
    // confused cluster ordering).
    if (totalDraftPhotos > 0) {
      const proceed = window.confirm(
        `importing will replace the current draft (${totalDraftPhotos} photos). already-saved clusters stay safe. continue?`,
      );
      if (!proceed) return;
    }
    setRescueBusy("import");
    setRescuePct(0);
    try {
      const restored = await importDraftZip(file, (pct) => setRescuePct(pct));
      // Push the undo snapshot only after a successful parse — a failed
      // import (bad ZIP, corrupted manifest) must not leave a phantom
      // "import draft" entry that restores identical state. Snapshot reads
      // stateRef.current, so this still captures the pre-import state.
      undo.push("import draft");
      // Revoke old preview blobs before replacing state to keep memory tidy.
      clusters.forEach((c) => c.photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)));
      unassigned.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      // Preserve saved clusters — they're the user's committed work.
      const kept = clusters.filter((c) => c.savedAt);
      setClusters([...kept, ...restored.clusters]);
      setUnassigned(restored.unassigned);
      setSelection({});
      if (typeof restored.gap === "number") setGap(restored.gap);
      const n =
        restored.clusters.reduce((a, c) => a + c.photos.length, 0) +
        restored.unassigned.length;
      toast.success(`draft imported · ${n} photos across ${restored.clusters.length} clusters.`);
    } catch (err) {
      console.error("draft import failed", err);
      toast.error(`import failed: ${err?.message || "unknown error"}`);
    } finally {
      setRescueBusy(null);
      setRescuePct(0);
    }
  }, [rescueBusy, totalDraftPhotos, clusters, unassigned, undo]);

  // Clamp the pagination cursor when clusters shrink (discard / clear / undo).
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(visibleClusters.length / PAGE_SIZE) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [visibleClusters.length, page, PAGE_SIZE]);

  // ---------- Cluster ops: split / merge / delete / drag between ----------
  const toggleSelectInCluster = (clusterId, photoId) => {
    setSelection((prev) => {
      const cur = new Set(prev[clusterId] || []);
      if (cur.has(photoId)) cur.delete(photoId); else cur.add(photoId);
      return { ...prev, [clusterId]: cur };
    });
  };
  const clearClusterSelection = (clusterId) => {
    setSelection((prev) => ({ ...prev, [clusterId]: new Set() }));
  };

  const splitCluster = (clusterId) => {
    const src = clusters.find((c) => c.id === clusterId);
    if (!src) return;
    const sel = selection[clusterId];
    if (!sel || sel.size === 0) { toast.warning("select photos to split off first."); return; }
    const moved = src.photos.filter((p) => sel.has(p.id));
    const stayed = src.photos.filter((p) => !sel.has(p.id));
    if (moved.length === 0 || stayed.length === 0) {
      toast.warning("split needs at least one photo on each side.");
      return;
    }
    undo.push("split cluster");
    // Use the photoUid counter so two rapid splits in the same ms don't collide
    // on React key / data-testid.
    const newCluster = { id: nextPhotoId().replace("p_", "c_") + "_split", photos: moved, targetItemId: null, savedAt: null };
    setClusters((prev) => {
      const idx = prev.findIndex((c) => c.id === clusterId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx], photos: stayed };
      next.splice(idx + 1, 0, newCluster);
      return next;
    });
    clearClusterSelection(clusterId);
    toast.success("cluster split.");
  };

  const mergeWithNext = (clusterId) => {
    const idx = clusters.findIndex((c) => c.id === clusterId);
    if (idx < 0 || idx === clusters.length - 1) { toast.warning("no cluster below to merge with."); return; }
    undo.push("merge clusters");
    setClusters((prev) => {
      const p = [...prev];
      const merged = { ...p[idx], photos: [...p[idx].photos, ...p[idx + 1].photos] };
      p.splice(idx, 2, merged);
      return p;
    });
    toast.success("merged.");
  };

  const dropCluster = (clusterId) => {
    // Don't revoke previewUrls here — undo needs the blobs to remain viewable.
    // On the next non-undo mutation the stale entries fall off history and are GC'd.
    undo.push("discard cluster");
    setClusters((prev) => prev.filter((c) => c.id !== clusterId));
  };

  const removePhotoFromCluster = (clusterId, photoId) => {
    undo.push("remove photo");
    setClusters((prev) => prev.map((c) => (
      c.id === clusterId ? { ...c, photos: c.photos.filter((p) => p.id !== photoId) } : c
    )).filter((c) => c.photos.length > 0));
  };

  const removePhotoFromUnassigned = (photoId) => {
    undo.push("remove photo");
    setUnassigned((prev) => prev.filter((p) => p.id !== photoId));
  };

  // ---------- Drag & drop between clusters / unassigned ----------
  // Locate a photo in EITHER clusters or unassigned. We resolve it OUTSIDE any
  // setState updater so both source-remove + destination-add are atomic (a
  // previous version resolved `moved` INSIDE setClusters and then dispatched
  // setUnassigned right after — under StrictMode / concurrent-render that
  // yielded a null `moved` and the photo was permanently lost).
  const findPhoto = (photoId) => {
    for (const c of clusters) {
      const hit = c.photos.find((p) => p.id === photoId);
      if (hit) return { photo: hit, source: "cluster", clusterId: c.id };
    }
    const u = unassigned.find((p) => p.id === photoId);
    if (u) return { photo: u, source: "unassigned" };
    return null;
  };
  const onDragStart = (e, photo) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ photoId: photo.id }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropOnCluster = (e, targetClusterId) => {
    e.preventDefault();
    // Saved clusters are frozen — accepting a drop would strand the new photo
    // (there's no re-attach control, and the badge count would silently lie).
    const target = clusters.find((c) => c.id === targetClusterId);
    if (!target || target.savedAt) return;
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    let data;
    try { data = JSON.parse(raw); } catch { return; }
    const found = findPhoto(data.photoId);
    if (!found) return;
    // No-op if dropping back on the same cluster.
    if (found.source === "cluster" && found.clusterId === targetClusterId) return;
    // Don't allow removing photos from a saved (frozen) cluster.
    if (found.source === "cluster") {
      const srcCluster = clusters.find((c) => c.id === found.clusterId);
      if (srcCluster?.savedAt) return;
    }

    if (found.source === "cluster") {
      undo.push("move photo");
      setClusters((prev) => prev.map((c) => {
        if (c.id === found.clusterId) return { ...c, photos: c.photos.filter((p) => p.id !== data.photoId) };
        if (c.id === targetClusterId) return { ...c, photos: [...c.photos, found.photo] };
        return c;
      }).filter((c) => c.photos.length > 0));
    } else {
      // From unassigned → cluster
      undo.push("move photo");
      setUnassigned((prev) => prev.filter((p) => p.id !== data.photoId));
      setClusters((prev) => prev.map((c) => (
        c.id === targetClusterId ? { ...c, photos: [...c.photos, found.photo] } : c
      )));
    }
  };
  const onDropOnUnassigned = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    let data;
    try { data = JSON.parse(raw); } catch { return; }
    const found = findPhoto(data.photoId);
    if (!found || found.source === "unassigned") return;
    undo.push("park photo");
    setClusters((prev) => prev.map((c) => (
      c.id === found.clusterId ? { ...c, photos: c.photos.filter((p) => p.id !== data.photoId) } : c
    )).filter((c) => c.photos.length > 0));
    setUnassigned((prev) => [...prev, found.photo]);
  };

  // ---------- Auto-suggest next SKU ----------
  // Memoise the candidate pool (candidate-stage items, sorted by created_at) so
  // we don't re-filter the whole inventory on every render.
  const candidatePool = useMemo(
    () => items
      .filter((i) => CANDIDATE_STAGES.includes(i.stage || ""))
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")),
    [items],
  );
  // Precompute a Map cluster.id → suggested item id in ONE pass instead of an
  // O(n²) walk per cluster on every render (the previous per-cluster suggestion
  // function was the dominant cause of dropdown focus loss + drag lag at scale).
  const suggestionMap = useMemo(() => {
    const usedIds = new Set(clusters.filter((c) => c.targetItemId).map((c) => c.targetItemId));
    const pool = candidatePool.filter((i) => !usedIds.has(i.id));
    const out = new Map();
    let anchorCreated = null;
    let unassignedRun = 0;
    let baseIdx = 0;
    for (const c of clusters) {
      if (c.targetItemId) {
        const anchor = items.find((it) => it.id === c.targetItemId);
        anchorCreated = anchor?.created_at || null;
        unassignedRun = 0;
        // Recompute baseIdx as first pool entry created after this new anchor.
        if (anchorCreated) {
          const idx = pool.findIndex((i) => (i.created_at || "") > anchorCreated);
          baseIdx = idx >= 0 ? idx : pool.length;
        }
        continue;
      }
      if (pool.length === 0) { out.set(c.id, null); continue; }
      const guess = pool[Math.min(baseIdx + unassignedRun, pool.length - 1)];
      out.set(c.id, guess?.id || null);
      unassignedRun += 1;
    }
    return out;
  }, [clusters, candidatePool, items]);
  const suggestedItemIdForCluster = (clusterId) => suggestionMap.get(clusterId) || null;

  // ---------- Save a cluster: upload photos + PUT inventory ----------
  const saveCluster = async (cluster) => {
    if (!cluster.targetItemId) { toast.warning("choose a piece first."); return { ok: false, reason: "no-target" }; }
    if (cluster.photos.length === 0) { toast.warning("no photos in cluster."); return { ok: false, reason: "empty" }; }

    // Always refetch the item RIGHT BEFORE the PUT. If two clusters target the
    // same SKU, saveAll's captured `items` closure is stale by the time cluster
    // #2 runs — using it would overwrite the photos cluster #1 just attached.
    // The server is the source of truth here.
    let target;
    try {
      const { data } = await api.get(`/inventory/${cluster.targetItemId}`);
      target = data;
    } catch (err) {
      console.error("[saveCluster] refetch failed for", cluster.targetItemId, err);
      toast.error(`could not load piece ${cluster.targetItemId?.slice(0, 6) || ""} — skipped.`);
      return { ok: false, reason: "refetch-failed" };
    }
    if (!target) { toast.error("piece no longer exists."); return { ok: false, reason: "not-found" }; }

    // Snapshot the item's photos BEFORE we PUT so cmd-Z can roll back the
    // server-side attach as well as the client cluster state.
    const preAttachPhotos = [...(target.photos || [])];
    const rollback = async () => {
      try {
        await api.put(`/inventory/${target.id}`, { photos: preAttachPhotos });
        await refetchItem(target.id);
        toast.info(`rolled back attach on ${target.sku || target.title}.`);
      } catch (err) {
        toast.error("could not roll back attach on the server — please review manually.");
        throw err;
      }
    };
    undo.push(`attach → ${target.sku || target.title || "piece"}`, rollback);

    setClusters((prev) => prev.map((c) => c.id === cluster.id ? { ...c, savedAt: "saving" } : c));

    try {
      // Bounded worker pool — CONCURRENCY=1 on mobile (iPhone Safari OOMs on
      // parallel decode+upload with 3-4MB HEIC-derived JPEGs) or 3 on desktop.
      const queue = [...cluster.photos];
      const paths = new Array(cluster.photos.length);
      let uploaded = 0;
      const uploadOne = async (photo, idx) => {
        const compressed = await compressImage(photo.file);
        const fd = new FormData();
        fd.append("file", compressed);
        // One-shot retry with a 2s pause. A transient hiccup (network blip,
        // brief origin memory spike right after the semaphore drains, CF 520)
        // no longer surfaces to the user as a red cluster error — the second
        // attempt almost always succeeds because the origin has recovered by
        // then. If BOTH attempts fail we bubble the error normally.
        let lastErr = null;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            paths[idx] = data.path;
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            const status = err?.response?.status;
            // Don't retry client-side errors (4xx) — they'll fail identically.
            // 413 (too large), 401 (auth), 400 (validation) are fatal.
            if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) throw err;
            if (attempt === 0) {
              console.warn(`[saveCluster] upload retry after transient error on ${photo.name}:`, err?.message);
              await new Promise((r) => setTimeout(r, 2000));
            }
          }
        }
        if (lastErr) throw lastErr;
        // Note: we intentionally DO NOT revoke photo.previewUrl here. Undoing
        // an attach or toggling "hide saved" back off should still show the
        // tiles. Blobs get freed on unmount (see unmount cleanup effect above).
        uploaded += 1;
        setUploadProgress({ current: uploaded, total: cluster.photos.length, sku: target.sku || target.title || "" });
      };
      let cursor = 0;
      const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, async () => {
        while (cursor < queue.length) {
          const my = cursor; cursor += 1;
          await uploadOne(queue[my], my);
        }
      });
      await Promise.all(workers);

      // preAttachPhotos is authoritative (from the fresh refetch above), so we
      // never clobber a prior cluster's attach even if two clusters share a SKU.
      const nextPhotos = [...preAttachPhotos, ...paths.filter(Boolean)];
      await api.put(`/inventory/${target.id}`, { photos: nextPhotos });

      setClusters((prev) => prev.map((c) => c.id === cluster.id ? { ...c, savedAt: new Date().toISOString(), lastError: null } : c));
      toast.success(`${paths.filter(Boolean).length} photos attached to ${target.sku || target.title}.`);
      await refetchItem(target.id);
      return { ok: true };
    } catch (e) {
      console.error("[saveCluster] attach failed for cluster", cluster.id, "target", cluster.targetItemId, e);
      setLastUploadFailureAt(Date.now()); // downgrade origin-health dot immediately
      const msg = e.response?.data?.detail || e.message || "unknown";
      // Persist the error onto the cluster so it stays visible next to the
      // failed cluster after a save-all sweep instead of a fleeting toast.
      setClusters((prev) => prev.map((c) => c.id === cluster.id ? { ...c, savedAt: null, lastError: msg } : c));
      toast.error(`attach failed on ${target.sku || target.title}: ${msg} — try again.`);
      return { ok: false, reason: "upload-failed", error: e };
    } finally {
      setUploadProgress(null);
    }
  };

  const unsavedClusters = clusters.filter((c) => !c.savedAt);
  const totalUnsavedPhotos = unsavedClusters.reduce((n, c) => n + c.photos.length, 0);
  const savedCount = clusters.filter((c) => c.savedAt && c.savedAt !== "saving").length;

  const saveAll = async () => {
    setBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      // Sequential — parallel would stampede the /upload endpoint with N×3
      // uploads AND compete for the same item's photos array. Each iteration
      // catches its own error so ONE bad cluster never bails the whole batch.
      // Snapshot the clusters at the start; work on the specific ids the user
      // saw at click-time (a later mutation shouldn't retroactively include
      // more clusters in this run).
      const toSave = clusters.filter((c) => !c.savedAt && c.targetItemId).map((c) => c.id);
      for (const cid of toSave) {
        // Re-read the latest cluster from state each iteration (photos array
        // may have changed via drag/split between clusters).
        const current = (stateRef.current.clusters || []).find((c) => c.id === cid);
        if (!current || current.savedAt || !current.targetItemId) continue;
        try {
          const res = await saveCluster(current);
          if (res?.ok) ok += 1; else failed += 1;
        } catch (err) {
          // Belt-and-suspenders: saveCluster should never throw, but if it
          // does we swallow so the loop continues.
          console.error("[saveAll] unexpected throw for cluster", cid, err);
          failed += 1;
        }
      }
      if (ok > 0 && failed === 0) toast.success(`saved ${ok} cluster${ok === 1 ? "" : "s"}.`);
      else if (ok > 0 && failed > 0) toast.warning(`saved ${ok}, ${failed} failed — check the toasts above.`);
      else if (ok === 0 && failed > 0) toast.error(`${failed} failed — none saved. check toasts / console.`);
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-8" data-testid="bulk-photos-page">
        {/* Header */}
        <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="editorial-label mb-1">inventory · atelier tools</p>
            <h1 className="font-serif text-4xl lowercase text-charcoal">bulk photo sorter</h1>
            <p className="text-sm text-espresso mt-2 max-w-2xl">
              drop your entire iphone shoot in one go. we&apos;ll cluster photos by exif timestamp so each burst becomes one candidate piece, then you assign a sku with one tap.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-espresso">
              <Clock className="w-3 h-3" />
              gap
              <input
                type="number"
                min={10} max={600} step={10}
                value={gap}
                onChange={(e) => setGap(Math.max(10, Math.min(600, Number(e.target.value) || DEFAULT_CLUSTER_GAP_S)))}
                className="w-16 bg-ivory border border-taupe/60 px-2 py-1 text-sm text-charcoal"
                data-testid="bp-gap-input"
                title="seconds between photos before we assume a new piece"
              />
              <span>sec</span>
            </label>
            {savedClusters.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-espresso cursor-pointer" data-testid="bp-hide-saved-label" title="hide already-attached clusters so the list only shows what still needs work">
                <input
                  type="checkbox"
                  checked={hideSaved}
                  onChange={(e) => setHideSaved(e.target.checked)}
                  className="h-3 w-3 accent-brass"
                  data-testid="bp-hide-saved-toggle"
                />
                hide saved ({savedClusters.length})
              </label>
            )}
            <OriginHealthDot lastFailureAt={lastUploadFailureAt} />
            <button
              onClick={() => setHistoryOpen(true)}
              className="border border-taupe hover:border-charcoal px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
              data-testid="bp-history-open"
              title="browse the last three draft snapshots (auto-saved on clear + export)"
            >
              <History className="w-3 h-3" /> history
            </button>
            <button
              onClick={() => undo.undo().then((label) => { if (label) toast.success(`undid: ${label}`); })}
              disabled={!undo.canUndo}
              className="border border-taupe hover:border-charcoal disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
              data-testid="bp-undo"
              title={undo.canUndo ? `undo · ${undo.lastLabel} (⌘Z)` : "nothing to undo"}
            >
              <Undo2 className="w-3 h-3" /> undo{undo.depth > 0 ? ` · ${undo.depth}` : ""}
            </button>
            <button
              onClick={() => undo.redo().then((label) => { if (label) toast.success(`redid: ${label}`); })}
              disabled={!undo.canRedo}
              className="border border-taupe hover:border-charcoal disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
              data-testid="bp-redo"
              title={undo.canRedo ? `redo · ${undo.nextRedoLabel} (⌘⇧Z / ctrl-y)` : "nothing to redo"}
            >
              <Redo2 className="w-3 h-3" /> redo{undo.redoDepth > 0 ? ` · ${undo.redoDepth}` : ""}
            </button>
            {totalDraftPhotos > 0 && (
              <button
                onClick={handleDownloadDraft}
                disabled={!!rescueBusy}
                className="border border-taupe hover:border-brass hover:text-brass disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
                data-testid="bp-download-draft"
                title={`download this draft as a ZIP · ${totalDraftPhotos} photo${totalDraftPhotos === 1 ? "" : "s"} — finish it on another device`}
              >
                {rescueBusy === "export" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                download draft · {totalDraftPhotos}
              </button>
            )}
            <button
              onClick={() => { if (!rescueBusy) rescueFileInputRef.current?.click(); }}
              disabled={!!rescueBusy}
              className="border border-taupe hover:border-brass hover:text-brass disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
              data-testid="bp-import-draft"
              title="import a rescue ZIP from another device"
            >
              {rescueBusy === "import" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageOpen className="w-3 h-3" />}
              import draft
            </button>
            <input
              ref={rescueFileInputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handleImportDraft(f);
              }}
              data-testid="bp-import-draft-input"
            />
            {(clusters.filter((c) => !c.savedAt).length > 0 || unassigned.length > 0) && (
              <button
                onClick={async () => {
                  if (!window.confirm("clear the draft? all unsaved clusters + unassigned photos will be discarded (already-attached pieces stay safe).")) return;
                  // Snapshot to rescue history BEFORE wiping so a mis-clicked
                  // clear can be recovered from the History panel weeks later,
                  // even after this tab has been closed.
                  await saveHistorySnapshot({ clusters, unassigned, gap, source: "clear" });
                  // Revoke every preview blob so we don't leak memory before dropping state.
                  clusters.forEach((c) => c.photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)));
                  unassigned.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                  setClusters([]);
                  setUnassigned([]);
                  setSelection({});
                  undo.clear();
                  await clearDraft();
                  toast.success("draft cleared — a snapshot is still recoverable in Rescue History.");
                }}
                className="border border-taupe hover:border-red-700 hover:text-red-700 px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
                data-testid="bp-clear-draft"
                title="discard the current draft (a snapshot is kept in rescue history)"
              >
                <Trash className="w-3 h-3" /> clear draft
              </button>
            )}
            <button
              onClick={saveAll}
              disabled={busy || unsavedClusters.filter((c) => c.targetItemId).length === 0}
              className="border border-brass text-brass hover:bg-brass hover:text-ivory disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brass px-4 py-2 text-xs tracking-widest uppercase flex items-center gap-2"
              data-testid="bp-save-all"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              save all assigned
            </button>
          </div>
        </header>

        {/* Live upload progress — critical on mobile so the client sees the
            tab is alive during the ~10-30s a HEIC-heavy attach can take. */}
        {uploadProgress && (
          <div className="bg-linen border border-brass/60 px-4 py-3 flex items-center gap-3 -mt-4" data-testid="bp-upload-progress">
            <Loader2 className="w-4 h-4 animate-spin text-brass flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-charcoal">
                uploading photo <strong>{uploadProgress.current}</strong> of <strong>{uploadProgress.total}</strong>
                {uploadProgress.sku ? <> → <span className="font-mono text-brass">{uploadProgress.sku}</span></> : null}
              </p>
              <div className="mt-1 h-1 bg-taupe/20 overflow-hidden">
                <div
                  className="h-full bg-brass transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Rescue export/import progress — big drafts can take 1-3 minutes to
            zip on mobile; without a visible bar the user assumes the tab is
            frozen and force-quits Safari mid-write. */}
        {rescueBusy && (
          <div className="bg-linen border border-brass/60 px-4 py-3 flex items-center gap-3 -mt-4" data-testid="bp-rescue-progress">
            <Loader2 className="w-4 h-4 animate-spin text-brass flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-charcoal">
                {rescueBusy === "export" ? "packaging draft into a ZIP…" : "reading draft ZIP…"} <strong>{rescuePct}%</strong>
              </p>
              <div className="mt-1 h-1 bg-taupe/20 overflow-hidden">
                <div
                  className="h-full bg-brass transition-all duration-300"
                  style={{ width: `${rescuePct}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Draft status line */}
        {(draftStatus.savedAt || draftStatus.saving) && (
          <p className="editorial-label flex items-center gap-2 -mt-4" data-testid="bp-draft-status">
            {draftStatus.saving ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> saving draft…</>
            ) : (
              <><Save className="w-3 h-3 text-brass" /> draft saved to this browser · {new Date(draftStatus.savedAt).toLocaleTimeString()}</>
            )}
          </p>
        )}

        {/* Broken-preview banner — surfaces the "safari truncated my IDB blobs"
            case explicitly. Without this, the user sees a wall of broken-image
            icons and no explanation of why or what to do about it. */}
        {brokenIds.size > 0 && (
          <div
            className="bg-red-50 border border-red-700/40 px-4 py-3 flex items-start gap-3 -mt-4"
            data-testid="bp-broken-banner"
          >
            <X className="w-4 h-4 text-red-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-red-900">
                <strong>{brokenIds.size}</strong> photo{brokenIds.size === 1 ? "" : "s"} couldn't be re-loaded from your browser's local storage
                {" "}— safari sometimes evicts large draft blobs after a tab reopen. re-drop those photos in the drop zone below, or discard them.
              </p>
            </div>
            <button
              onClick={() => {
                if (!window.confirm(`discard ${brokenIds.size} unreadable photo${brokenIds.size === 1 ? "" : "s"} from this draft? you can still re-drop them via the drop zone.`)) return;
                undo.push("discard broken");
                setClusters((prev) => prev
                  .map((c) => ({ ...c, photos: c.photos.filter((p) => !brokenIds.has(p.id)) }))
                  .filter((c) => c.photos.length > 0 || c.savedAt));
                setUnassigned((prev) => prev.filter((p) => !brokenIds.has(p.id)));
                setBrokenIds(new Set());
                toast.success("unreadable photos discarded.");
              }}
              className="border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory px-3 py-1.5 text-[0.6rem] tracking-widest uppercase flex-shrink-0"
              data-testid="bp-discard-broken"
            >
              discard broken
            </button>
          </div>
        )}

        {/* Drop zone */}
        <label
          className="block border-2 border-dashed border-taupe/60 hover:border-brass hover:bg-brass/5 transition-colors bg-linen p-10 text-center cursor-pointer"
          data-testid="bp-dropzone"
        >
          <input
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => { ingest(e.target.files); e.target.value = ""; }}
            data-testid="bp-file-input"
          />
          <Upload className="w-6 h-6 text-brass mx-auto mb-2" />
          <p className="editorial-label">drop the whole shoot here</p>
          <p className="text-xs text-espresso mt-1">bursts of ~6 photos taken close together will auto-group into one piece.</p>
        </label>

        {/* Summary */}
        {(clusters.length > 0 || unassigned.length > 0) && (
          <div className="flex flex-wrap items-center gap-6 text-xs text-espresso" data-testid="bp-summary">
            <span><strong className="text-charcoal">{clusters.length}</strong> clusters</span>
            <span><strong className="text-charcoal">{totalUnsavedPhotos}</strong> photos pending</span>
            <span><strong className="text-charcoal">{savedCount}</strong> saved</span>
            {(() => {
              const failedCount = clusters.filter((c) => c.lastError && !c.savedAt).length;
              if (failedCount === 0) return null;
              return (
                <span className="text-red-800" data-testid="bp-failed-summary">
                  <strong>{failedCount}</strong> failed — see red-bordered clusters
                </span>
              );
            })()}
            {unassigned.length > 0 && <span className="text-brass"><strong>{unassigned.length}</strong> without exif time — drag into a cluster</span>}
          </div>
        )}

        {/* Clusters — paginated at PAGE_SIZE so a 400-photo shoot doesn't lock
            the tab trying to render 60+ cluster cards at once. */}
        {visibleClusters.length > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-b border-taupe/30 py-3" data-testid="bp-pagination">
            <p className="text-xs text-espresso">
              showing <strong className="text-charcoal">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, visibleClusters.length)}</strong> of <strong className="text-charcoal">{visibleClusters.length}</strong> clusters
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border border-taupe hover:border-charcoal disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 text-[0.65rem] tracking-widest uppercase"
                data-testid="bp-page-prev"
              >
                ← prev
              </button>
              <span className="text-xs text-taupe px-2">page {page + 1} / {Math.ceil(visibleClusters.length / PAGE_SIZE)}</span>
              <button
                onClick={() => setPage((p) => Math.min(Math.ceil(visibleClusters.length / PAGE_SIZE) - 1, p + 1))}
                disabled={(page + 1) * PAGE_SIZE >= visibleClusters.length}
                className="border border-taupe hover:border-charcoal disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 text-[0.65rem] tracking-widest uppercase"
                data-testid="bp-page-next"
              >
                next →
              </button>
            </div>
          </div>
        )}
        <div className="space-y-4">
          {visibleClusters.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((c) => {
            const sel = selection[c.id] || new Set();
            const suggestedId = suggestedItemIdForCluster(c.id);
            const isSaved = c.savedAt && c.savedAt !== "saving";
            const isSaving = c.savedAt === "saving";
            const targetItem = items.find((i) => i.id === c.targetItemId);
            return (
              <div
                key={c.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropOnCluster(e, c.id)}
                className={`border p-5 space-y-4 ${isSaved ? "bg-linen border-brass/40" : c.lastError ? "bg-ivory border-red-700/50" : "bg-ivory border-taupe/50"}`}
                data-testid={`bp-cluster-${c.id}`}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <GripVertical className="w-4 h-4 text-taupe mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-[260px]">
                    <SkuPicker
                      items={items}
                      value={c.targetItemId}
                      onChange={(id) => setClusters((prev) => prev.map((x) => x.id === c.id ? { ...x, targetItemId: id } : x))}
                      testid={`bp-skupick-${c.id}`}
                      autoSuggestId={suggestedId}
                      disabled={isSaved || isSaving}
                    />
                  </div>
                  {isSaved ? (
                    <span className="border border-brass text-brass px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1" data-testid={`bp-cluster-saved-${c.id}`}>
                      <Check className="w-3 h-3" /> saved · {c.photos.length} photos → {targetItem?.sku}
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => saveCluster(c)}
                        disabled={!c.targetItemId || isSaving}
                        className="border border-brass text-brass hover:bg-brass hover:text-ivory disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
                        data-testid={`bp-cluster-save-${c.id}`}
                      >
                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                        {isSaving ? "saving" : "attach"}
                      </button>
                      <button
                        onClick={() => splitCluster(c.id)}
                        disabled={sel.size === 0}
                        className="border border-taupe hover:border-charcoal disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
                        data-testid={`bp-cluster-split-${c.id}`}
                        title="split selected photos into a new cluster"
                      >
                        <Scissors className="w-3 h-3" /> split ({sel.size})
                      </button>
                      <button
                        onClick={() => mergeWithNext(c.id)}
                        className="border border-taupe hover:border-charcoal px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
                        data-testid={`bp-cluster-merge-${c.id}`}
                        title="merge with cluster below"
                      >
                        <Merge className="w-3 h-3" /> merge next
                      </button>
                      <button
                        onClick={() => dropCluster(c.id)}
                        className="border border-taupe hover:border-red-700 hover:text-red-700 px-3 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1"
                        data-testid={`bp-cluster-drop-${c.id}`}
                        title="discard entire cluster"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {c.photos.map((p) => (
                    <PhotoTile
                      key={p.id}
                      photo={p}
                      onRemove={isSaved ? null : (id) => removePhotoFromCluster(c.id, id)}
                      selected={sel.has(p.id)}
                      onToggleSelect={isSaved ? null : (id) => toggleSelectInCluster(c.id, id)}
                      onDragStart={isSaved ? null : onDragStart}
                      onBrokenPreview={markBroken}
                      broken={brokenIds.has(p.id)}
                    />
                  ))}
                </div>
                {c.lastError && !isSaved && (
                  <p className="text-xs text-red-800 bg-red-50 border border-red-700/30 px-3 py-2" data-testid={`bp-cluster-error-${c.id}`}>
                    <strong>last attempt failed:</strong> {c.lastError}
                  </p>
                )}
                {(() => {
                  const ts = c.photos.map((p) => p.takenAt).filter((t) => t != null);
                  if (ts.length === 0) return null;
                  const min = Math.min(...ts);
                  const max = Math.max(...ts);
                  return (
                    <p className="text-[0.65rem] uppercase tracking-widest text-taupe">
                      shot window: {new Date(min).toLocaleTimeString()}{min !== max ? ` – ${new Date(max).toLocaleTimeString()}` : ""}
                    </p>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Unassigned strip — always rendered so photos can be parked here even
            when the initial ingest produced 0 exif-less photos. */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropOnUnassigned}
          className="border-2 border-dashed border-brass/40 bg-brass/5 p-5"
          data-testid="bp-unassigned"
        >
          <div className="flex items-center gap-2 mb-3">
            <Images className="w-4 h-4 text-brass" />
            <p className="editorial-label">
              unassigned · {unassigned.length === 0 ? "drag a photo here to park it" : `drag into any cluster or drop back here (${unassigned.length})`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 min-h-[6rem]">
            {unassigned.length === 0 ? (
              <p className="text-xs text-taupe italic self-center">empty — photos without exif time land here.</p>
            ) : (
              unassigned.map((p) => (
                <PhotoTile
                  key={p.id}
                  photo={p}
                  onRemove={removePhotoFromUnassigned}
                  onDragStart={onDragStart}
                  onBrokenPreview={markBroken}
                  broken={brokenIds.has(p.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Helpful hint when clusters exist but are all saved & hidden */}
        {clusters.length > 0 && visibleClusters.length === 0 && hideSaved && (
          <div className="text-center py-10 border border-brass/30 bg-brass/5" data-testid="bp-all-hidden">
            <p className="editorial-label text-brass">all {savedClusters.length} clusters saved · hidden from list</p>
            <p className="text-xs text-espresso mt-2">untick <em>hide saved</em> above to view them again.</p>
          </div>
        )}

        {/* Empty state */}
        {clusters.length === 0 && unassigned.length === 0 && (
          <div className="text-center py-20 border border-taupe/30 bg-linen/50">
            <Images className="w-8 h-8 text-taupe mx-auto mb-3" />
            <p className="editorial-label">no photos yet — drop a shoot above to begin.</p>
          </div>
        )}

        <RescueHistoryPanel
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          onRestore={(snap) => {
            // Snapshot the current live state onto the undo stack so a
            // mis-clicked "Restore" is still reversible via ⌘Z.
            undo.push("restore history");
            // Revoke previews of any state we're about to replace so we don't
            // leak Blob refs. Preserve saved clusters — they're server-committed
            // work and shouldn't be blown away by a history restore.
            clusters.filter((c) => !c.savedAt).forEach((c) =>
              c.photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)),
            );
            unassigned.forEach((p) => URL.revokeObjectURL(p.previewUrl));
            const kept = clusters.filter((c) => c.savedAt);
            setClusters([...kept, ...snap.clusters]);
            setUnassigned(snap.unassigned);
            setSelection({});
            if (typeof snap.gap === "number") setGap(snap.gap);
            const n =
              snap.clusters.reduce((a, c) => a + c.photos.length, 0) + snap.unassigned.length;
            toast.success(`restored · ${n} photos, ${snap.clusters.length} clusters.`);
          }}
        />
      </div>
    </AdminLayout>
  );
}

export default function AdminBulkPhotos() {
  return (
    <BulkSorterErrorBoundary>
      <AdminBulkPhotosInner />
    </BulkSorterErrorBoundary>
  );
}
